import mergeClasses from '../../../utils/shallowMerge';
import InlineMessage from '../inlineMessage/InlineMessage';
import defaultClasses from './TextField.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, {
  createElement,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

export const TextField = forwardRef((props, ref) => {
  const {
    id: idProp = '',
    defaultValue = '',
    className = '',
    isRequired = false,
    label = '',
    variant = 'underlined',
    labelPosition = 'normal',
    placeholder = '',
    inputMode = 'text',
    type = 'text',
    textAlign = 'left',
    isFocused = false,
    iconBefore = '',
    iconAfter = '',
    onFocus = () => {},
    onChange = () => {},
    onBlur = () => {},
    onKeyDown,
    autoFocus = false,
    multiline = false,
    prefix = '',
    maxLength = 1000,
    error = false,
    isDisabled = false,
    isReadOnly = false,
    errorMessage = '',
    warning,
    warningMessage,
    successMessage = '',
    name = '',
    customClasses = null,
    autoComplete = 'disabled',
    borderColor,
    textareaRef,
    isFieldFilled,
  } = props;

  const styles = mergeClasses(defaultClasses, customClasses);
  const fieldRef = useRef();
  const elRef = useRef();
  const [hasValue, setHasValue] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const [inputState, setInputState] = useState(
    defaultValue && defaultValue.length ? 'filled' : null,
  );
  const [characterCount, setCharacterCount] = useState(
    (defaultValue && defaultValue.length) || 0,
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (autoFocus) {
      setInputState('focused');
    }
  }, [autoFocus]);

  const defaultInputElement = multiline ? 'textarea' : 'input';

  useEffect(() => {
    setHasValue(Boolean(elRef?.current?.value));
  }, [elRef, elRef?.current?.value, isFieldFilled]);

  const customInputClasses = clsx(
    styles.inputWrapper,
    styles.inputField,
    styles.inputFieldCustom,
    {
      [` ${styles.focused}`]: isFocused || inputState === 'focused',
      [` ${styles.labelPositionNormal}`]:
        labelPosition === 'normal' && variant === 'standard',
      [` ${styles.labelPositionInset}`]:
        labelPosition === 'inset' &&
        (variant === 'standard' || variant === 'underlined'),
      [` ${styles.labelPositionDynamic}`]:
        labelPosition === 'dynamic' &&
        (variant === 'standard' || variant === 'underlined'),
      [` ${styles.hasValue}`]: characterCount !== 0 || hasValue,
      [` ${styles.inputOutlined}`]: variant === 'standard',
      [` ${styles.inputUnderlined}`]: variant === 'underlined',
      [` ${styles.inputFilled}`]: variant === 'filled',
      [` ${styles.textCentered}`]: textAlign === 'center',
      [` ${styles.withError}`]: error,
      [` ${styles.withIconLeft}`]: iconBefore,
      [` ${styles.withIconRight}`]: iconAfter,
    },
    className,
  );

  if (!isMounted) {
    return <div suppressHydrationWarning />;
  }

  return (
    <div className={customInputClasses} ref={ref}>
      {label && (
        <label className={styles.label} htmlFor={idProp} disabled={isDisabled}>
          {label}
        </label>
      )}
      <div
        className={clsx(styles.fieldControl, {
          [` ${styles.multiline}`]: multiline,
        })}
        ref={fieldRef}
      >
        {iconBefore && <div className={styles.iconBefore}>{iconBefore}</div>}
        {prefix && <div className={styles.prefix}>{prefix}</div>}
        {createElement(defaultInputElement, {
          ref: (inputRef) => {
            if (textareaRef) textareaRef.current = inputRef;
            elRef.current = inputRef;
            ref && ref instanceof Function ? ref(inputRef) : inputRef;
          },
          id: idProp,
          name,
          type,
          autoComplete,
          inputMode,
          autoFocus,
          disabled: isDisabled,
          readOnly: isReadOnly,
          maxLength,
          required: isRequired,
          placeholder,
          className: clsx(styles.inputGeneral, {
            [` ${styles.withoutLabel}`]: !label && variant === 'underlined',
            [` ${styles.textArea}`]: multiline,
            [` ${styles.input}`]: !multiline,
            [` ${styles.borderNone}`]: type === 'hidden',
          }),
          onFocus: (event) => {
            if (!inputState) {
              setInputState('focused');
            }
            onFocus && onFocus(event);
          },
          onBlur: (event) => {
            if (inputState) {
              setInputState(null);
            }
            onBlur && onBlur(event);
          },
          onKeyDown: (event) => {
            onKeyDown && onKeyDown(event);
          },
          onChange: (event) => {
            // Workaround for systems where maxlength prop is applied onBlur (https://caniuse.com/#feat=maxlength)
            if (
              maxLength === undefined ||
              event.target.value.length <= maxLength
            ) {
              setHasValue(Boolean(event.target.value));
              setCharacterCount(event.target.value.length);
              onChange && onChange(event);
            } else {
              event.stopPropagation();
              event.preventDefault();
            }
          },
          defaultValue,
          ...(error && { 'aria-invalid': true }),
        })}
        {multiline && (
          <span className={styles.characterCount}>
            Character Count:
            {characterCount}
          </span>
        )}
        {iconAfter && <div className={styles.iconAfter}>{iconAfter}</div>}
        <fieldset
          style={{ '--default-border-color': borderColor }}
          className={styles.shadow}
        />
      </div>
      {error && errorMessage ? (
        <InlineMessage
          type="error"
          message={errorMessage}
          className={customClasses?.error}
        />
      ) : null}
      {((!error && warning) && warningMessage) ? (
        <InlineMessage
          type="warning"
          message={warningMessage}
          className={customClasses?.warning}
        />
      ) : null}
      {successMessage ? (
        <InlineMessage type="success" message={successMessage} />
      ) : null}
    </div>
  );
});

TextField.propTypes = {
  id: PropTypes.string,
  defaultValue: PropTypes.string,
  className: PropTypes.string,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['standard', 'underlined', 'filled', 'password']),
  labelPosition: PropTypes.oneOf(['normal', 'inset', 'dynamic']),
  inputMode: PropTypes.oneOf([
    'text',
    'decimal',
    'numeric',
    'tel',
    'search',
    'email',
    'url',
  ]),
  type: PropTypes.oneOf([
    'text',
    'password',
    'number',
    'tel',
    'email',
    'hidden',
    'date',
    'search',
    'time',
    'url',
  ]),
  textAlign: PropTypes.oneOf(['left', 'center']),
  isFocused: PropTypes.bool,
  iconBefore: PropTypes.node,
  iconAfter: PropTypes.node,
  autoFocus: PropTypes.bool,
  multiline: PropTypes.bool,
  prefix: PropTypes.node,
  maxLength: PropTypes.number,
  error: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  successMessage: PropTypes.string,
  placeholder: PropTypes.string,
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  customClasses: PropTypes.shape({}),
};
